import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import api from '../lib/api'
import type { User, LoginRequest, RegisterRequest, TokenResponse } from '../types'

interface AuthState {
  accessToken: string | null
  refreshToken: string | null
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  
  // Actions
  login: (data: LoginRequest) => Promise<void>
  register: (data: RegisterRequest) => Promise<void>
  logout: () => void
  setTokens: (access: string, refresh: string) => void
  loadUser: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      
      login: async (data: LoginRequest) => {
        set({ isLoading: true })
        try {
          const response = await api.post<TokenResponse>('/auth/login', data)
          const { access_token, refresh_token } = response.data
          
          set({
            accessToken: access_token,
            refreshToken: refresh_token,
            isAuthenticated: true,
          })
          
          // Load user info
          await get().loadUser()
        } finally {
          set({ isLoading: false })
        }
      },
      
      register: async (data: RegisterRequest) => {
        set({ isLoading: true })
        try {
          const response = await api.post<TokenResponse>('/auth/register', data)
          const { access_token, refresh_token } = response.data
          
          set({
            accessToken: access_token,
            refreshToken: refresh_token,
            isAuthenticated: true,
          })
          
          // Load user info
          await get().loadUser()
        } finally {
          set({ isLoading: false })
        }
      },
      
      logout: () => {
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isAuthenticated: false,
        })
      },
      
      setTokens: (access: string, refresh: string) => {
        set({
          accessToken: access,
          refreshToken: refresh,
          isAuthenticated: true,
        })
      },
      
      loadUser: async () => {
        try {
          const response = await api.get<User>('/auth/me')
          set({ user: response.data })
        } catch {
          // Token might be invalid
          get().logout()
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
